import { formatTimeUntilAutoCancel, formatTimeUntilBusinessDeadline } from '@/lib/business-hours';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';

interface PaymentInstructionsProps {
  purchaseId: string;
  totalAmount: number;
  organizerName?: string;
  deadlineHours?: number | null;
  createdAt?: string | Date;
  seatCount?: number;
}

export default function PaymentInstructions({
  purchaseId,
  totalAmount,
  organizerName = 'UNIVERSIDAD AUTÓNOMA DE CHIAPAS',
  deadlineHours = null,
  createdAt,
  seatCount,
}: PaymentInstructionsProps) {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(`${label} copiado al portapapeles`);
    });
  };

  // Datos bancarios para transferencias (modificar según tus datos reales)
  const bankingInfo = {
    alias: 'Programa Alumni',
    bankName: 'SANTANDER',
    accountType: 'Cuenta de Cheques',
    accountNumber: '65-51130685-3',
    clabe: '014100655113068539',
    beneficiary: organizerName,
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
      <div className="flex items-center mb-4">
        <svg
          className="h-8 w-8 text-blue-600 mr-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="text-2xl font-bold text-gray-900">Instrucciones de Pago</h3>
      </div>

      <div className="bg-white rounded-lg p-5 shadow-sm mb-4">
        <p className="text-gray-700 mb-4">
          Realiza una transferencia bancaria por el monto total a la siguiente cuenta:
        </p>

        {/* Límite de confirmación de pago (horas hábiles) */}
        {createdAt && (
          <div className="bg-yellow-50 rounded-lg p-4 mb-4 border-l-4 border-yellow-600">
            {(() => {
              const info =
                deadlineHours && deadlineHours > 0
                  ? formatTimeUntilBusinessDeadline(createdAt, deadlineHours)
                  : formatTimeUntilAutoCancel(createdAt, seatCount);
              return (
                <>
                  <p className="text-sm text-yellow-800 mb-1">
                    {deadlineHours && deadlineHours > 0 ? (
                      <>
                        Debes confirmar este pago dentro de{' '}
                        <strong className="font-bold">{info.formatted}</strong>.
                      </>
                    ) : (
                      <>
                        Este pago será cancelado automáticamente si no es confirmado dentro de{' '}
                        <strong className="font-bold">{info.formatted}</strong>.
                      </>
                    )}
                  </p>
                  <p className="text-xs text-yellow-700">
                    Horas hábiles: Lunes a Viernes, 8:00 AM – 5:00 PM (hora México)
                  </p>
                  {deadlineHours && deadlineHours > 0 ? (
                    <p className="text-xs text-yellow-700">
                      (Límite: {info.limitHours} hora{info.limitHours !== 1 ? 's' : ''} hábil
                      {info.limitHours !== 1 ? 'es' : ''} establecido para este evento)
                    </p>
                  ) : null}
                </>
              );
            })()}
          </div>
        )}

        {/* Monto a Pagar */}
        <div className="bg-blue-50 rounded-lg p-4 mb-4 border-l-4 border-blue-600">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium text-gray-700">Monto a Pagar:</span>
            <span className="text-3xl font-bold text-blue-600">{formatCurrency(totalAmount)}</span>
          </div>
        </div>

        {/* Datos Bancarios */}
        <div className="space-y-3">
          <DataRow
            label="Banco"
            value={bankingInfo.bankName}
            onCopy={() => copyToClipboard(bankingInfo.bankName, 'Banco')}
          />
          <DataRow
            label="Beneficiario"
            value={bankingInfo.beneficiary}
            onCopy={() => copyToClipboard(bankingInfo.beneficiary, 'Beneficiario')}
          />
          <DataRow
            label="Número de Cuenta"
            value={bankingInfo.accountNumber}
            onCopy={() => copyToClipboard(bankingInfo.accountNumber, 'Número de cuenta')}
            highlight
          />
          <DataRow
            label="CLABE Interbancaria"
            value={bankingInfo.clabe}
            onCopy={() => copyToClipboard(bankingInfo.clabe, 'CLABE')}
            highlight
          />
          <DataRow
            label="Alias"
            value={bankingInfo.alias}
            onCopy={() => copyToClipboard(bankingInfo.alias, 'Alias')}
          />
        </div>

        {/* Referencia */}
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Importante: Incluye esta referencia en tu transferencia
            </span>
          </p>
          <div className="flex justify-between items-center">
            <code className="text-lg font-mono font-bold text-gray-900">
              {purchaseId.split('-')[0].toUpperCase()}
            </code>
            <button
              onClick={() => copyToClipboard(purchaseId.split('-')[0].toUpperCase(), 'Referencia')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Copiar
            </button>
          </div>
        </div>
      </div>

      {/* Pasos siguientes */}
      <div className="bg-white rounded-lg p-5 shadow-sm">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <svg
            className="h-5 w-5 text-green-600 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Pasos Siguientes:
        </h4>
        <ol className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start">
            <span className="font-bold text-blue-600 mr-2">1.</span>
            <span>Realiza la transferencia bancaria por el monto exacto</span>
          </li>
          <li className="flex items-start">
            <span className="font-bold text-blue-600 mr-2">2.</span>
            <span>Guarda tu comprobante de pago (captura o PDF)</span>
          </li>
          <li className="flex items-start">
            <span className="font-bold text-blue-600 mr-2">3.</span>
            <span>Sube el comprobante en la sección de abajo</span>
          </li>
          <li className="flex items-start">
            <span className="font-bold text-blue-600 mr-2">4.</span>
            <span>
              Espera la confirmación por email. La revisión y confirmación se realiza en horas
              hábiles.
            </span>
          </li>
          <li className="flex items-start">
            <span className="font-bold text-blue-600 mr-2">5.</span>
            <span>Recibe tus tickets con código QR por email</span>
          </li>
        </ol>
      </div>
    </div>
  );
}

// Componente auxiliar para las filas de datos
interface DataRowProps {
  label: string;
  value: string;
  onCopy: () => void;
  highlight?: boolean;
}

function DataRow({ label, value, onCopy, highlight = false }: DataRowProps) {
  return (
    <div
      className={`flex justify-between items-center p-3 rounded ${
        highlight ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
      }`}
    >
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className={`font-mono ${highlight ? 'font-bold text-blue-900' : 'text-gray-900'}`}>
          {value}
        </p>
      </div>
      <button
        onClick={onCopy}
        className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
        title="Copiar"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      </button>
    </div>
  );
}
